import React, { useMemo, useRef } from "react";
import * as styles from "./BrochureFilters.module.scss";
import {
  BarCheckbox,
  Chip,
  DropDown,
  useSticky
} from "@silversea-ssc/ui-components-react";
import clsx from "clsx";
import { t } from "@lingui/core/macro";
import { formatBrochuresPlural } from "src/utils/common-plurals";

export type BrochureGroupFilterItem = {
  id: string;
  title: string;
  count: number;
};

type BrochureFiltersProps = {
  onFilterChange: (groupId: string) => void;
  selectedFilter: string;
  brochureGroups: BrochureGroupFilterItem[];
};

export const BrochureFilters: React.FC<BrochureFiltersProps> = ({
  onFilterChange,
  selectedFilter,
  brochureGroups
}) => {
  const filterRef = useRef(null);
  const isSticky = useSticky(filterRef, 10);

  return (
    <div
      ref={filterRef}
      className={clsx(styles.container, { [styles.sticky]: isSticky })}
      data-content-name="brochure_filters"
    >
      {brochureGroups.map((group) => {
        const isSelected = selectedFilter === group.id;
        return (
          <button
            key={group.id}
            onClick={() => onFilterChange(group.id)}
            className="filter-button"
            data-content-piece={`filter_${group.title}`}
          >
            <Chip
              background={isSelected ? "titanium" : "transparent"}
              className={clsx(styles.filterChip, {
                [styles.selected]: isSelected
              })}
            >
              {`${group.title} (${group.count})`}
            </Chip>
          </button>
        );
      })}
    </div>
  );
};

export const MobileBrochureFilters: React.FC<BrochureFiltersProps> = ({
  onFilterChange,
  selectedFilter,
  brochureGroups
}) => {
  const filterRef = useRef(null);
  const isSticky = useSticky(filterRef, 10);

  const selectedFilterLabel = useMemo(() => {
    const selectedGroup = brochureGroups.find(
      (group) => group.id === selectedFilter
    );
    return selectedGroup
      ? `${selectedGroup.title} (${selectedGroup.count})`
      : "";
  }, [selectedFilter, brochureGroups]);

  return (
    <div
      className={clsx(styles.mobileContainer, { [styles.sticky]: isSticky })}
      ref={filterRef}
    >
      <DropDown
        label={t`Filters`}
        selectedValuesLabel={selectedFilterLabel}
        expandOnMobile="absolute"
        expandOnTablet="absolute"
        expandOnDesktop="absolute"
        closeOnClickOutside={true}
        className={styles.dropdown}
        contentWrapperClassName={styles.dropdownContent}
        size={"big"}
        dataContentName="brochure_filters_dropdown"
        dataContentPiece="brochure_filters_dropdown_toggled"
      >
        <div>
          {brochureGroups.map((group) => {
            return (
              <BarCheckbox
                key={group.id}
                label={group.title}
                dataContentPiece={`filter_${group.title}`}
                counterLabel={
                  group.count > 0
                    ? formatBrochuresPlural(group.count).toLowerCase()
                    : undefined
                }
                checked={selectedFilter === group.id}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={(_) => onFilterChange(group.id)}
              />
            );
          })}
        </div>
      </DropDown>
    </div>
  );
};
